import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader } from "Common/Loader";
import { getAnnouncementList, getPublicAnnouncements } from "../../helpers/backend_helper";

interface Announcement {
  announcementId: number;
  title: string;
  type: string;
  startDate: string;
  endDate: string;
  shortDescription: string;
}

const ViewAnnouncements = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getTypeClass = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'important':
        return 'bg-blue-100 text-blue-800';
      case 'event':
        return 'bg-green-100 text-green-800';
      case 'emergency':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getBorderColor = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'important':
        return 'border-blue-600';
      case 'event':
        return 'border-green-600';
      case 'emergency':
        return 'border-red-600';
      default:
        return 'border-gray-600';
    }
  };

  useEffect(() => {
    const loadAnnouncements = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await getPublicAnnouncements();
        
        if (response?.data) {
          setAnnouncements(response.data);
        } else {
          setError('No announcements found');
        }
      } catch (error) {
        console.error('Error loading announcements:', error);
        setError('Failed to load announcements. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    loadAnnouncements();
  }, []);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-96">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="mb-4 p-4 text-sm text-red-800 rounded-lg bg-red-50 dark:bg-red-900/50 dark:text-red-200" role="alert">
            {error}
            <button 
              type="button" 
              className="ml-2 text-red-800 hover:text-red-900 dark:text-red-200 dark:hover:text-red-100"
              onClick={() => window.location.reload()}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Announcements</h1>
        <Link
          to="/"
          className="text-blue-600 font-medium hover:text-blue-800 transition"
        >
          ← Back to Home
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {announcements.map((announcement) => (
          <div 
            key={announcement.announcementId}
            className={`bg-white rounded-xl shadow-md overflow-hidden border-l-4 ${getBorderColor(announcement.type)}`}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <span className={`${getTypeClass(announcement.type)} text-xs font-semibold px-2.5 py-0.5 rounded`}>
                  {announcement.type || 'Event'}
                </span>
                <span className="text-gray-500 text-sm">
                  {new Date(announcement.startDate).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                {announcement.title}
              </h3>
              
              <p className="text-gray-600 mb-4">
                {announcement.shortDescription}
              </p>

              <Link
                to={`/announcements/${announcement.announcementId}`}
                className="inline-flex items-center text-blue-600 hover:text-blue-800"
              >
                Read More
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          </div>
        ))}
      </div>

      {announcements.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">No announcements available at the moment.</p>
        </div>
      )}
    </div>
  );
};

export default ViewAnnouncements; 